'use client';

import type { RowData } from '@tanstack/react-table';
import type { Table } from '@/lib/table-features';
import { useTranslations } from 'next-intl';
import { RotateCcw, Settings2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import * as React from 'react';
import { CheckIcon, CaretSortIcon } from '@radix-ui/react-icons';
import { usePermissions } from '@/hooks/use-permissions';

interface DataTableViewOptionsProps<TData extends RowData> {
  table: Table<TData>;
  /**
   * Permission required to render the View Columns button at all.
   * Convention: `{group}.view_filter` (e.g. `users.view_filter`).
   * When unset, the button is always visible.
   */
  viewFilterPermission?: string;
}

export function DataTableViewOptions<TData extends RowData>({
  table,
  viewFilterPermission
}: DataTableViewOptionsProps<TData>) {
  const { hasPermission } = usePermissions();
  if (viewFilterPermission && !hasPermission(viewFilterPermission)) {
    return null;
  }
  return <DataTableViewOptionsInner table={table} />;
}

function DataTableViewOptionsInner<TData extends RowData>({
  table
}: {
  table: Table<TData>;
}) {
  const tc = useTranslations('common');
  const columns = React.useMemo(
    () => table.getAllColumns().filter((column) => column.getCanHide()),
    [table]
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          aria-label='Toggle columns'
          role='combobox'
          variant='outline'
          size='sm'
          className='ms-auto hidden h-8 lg:flex'
        >
          <Settings2 />
          {tc('viewColumns')}
          <CaretSortIcon className='ms-auto opacity-50' />
        </Button>
      </PopoverTrigger>
      <PopoverContent align='end' className='w-44 p-0'>
        <Command>
          <CommandInput placeholder={tc('searchColumns')} />
          <CommandList>
            <CommandEmpty>{tc('noColumnsFound')}</CommandEmpty>
            <CommandGroup>
              {columns.map((column) => (
                <CommandItem
                  key={column.id}
                  onSelect={() =>
                    column.toggleVisibility(!column.getIsVisible())
                  }
                >
                  <span className='truncate'>
                    {column.columnDef.meta?.label ??
                      (typeof column.columnDef.header === 'string'
                        ? column.columnDef.header
                        : column.id)}
                  </span>
                  <CheckIcon
                    className={cn(
                      'ms-auto size-4 shrink-0',
                      column.getIsVisible() ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
        {table.options.meta?.resetTableState && (
          <>
            <Separator />
            <div className='p-1'>
              <Button
                variant='ghost'
                size='sm'
                className='w-full justify-start'
                onClick={() => table.options.meta?.resetTableState?.()}
              >
                <RotateCcw className='size-4' />
                {tc('resetTable')}
              </Button>
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
